import { useState } from 'react';
import { Calendar, HelpCircle, Star, ListTodo, Plane, X, Copy, Check, Bed } from 'lucide-react';
import { DraggableModal } from './DraggableModal';

const ExportCalendarModal = ({ user, profile, saveUserProfile, onClose }) => {
  const [name, setName] = useState('My NYTM Calendar');
  const [note, setNote] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([
    'nytm-event',
    'nytm-tentative',
    'nytm-tasks-starred',
    'nytm-tasks-h',
    'nytm-tasks-m',
    'nytm-tasks-l',
    'nytm-flight',
    'nytm-hotel'
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const options = [
    { id: 'nytm-event', label: 'Events', icon: Calendar, color: 'text-blue-500' },
    { id: 'nytm-tentative', label: 'Tentative Events', icon: HelpCircle, color: 'text-indigo-500' },
    { id: 'nytm-tasks-starred', label: 'Project Stars', icon: Star, color: 'text-yellow-500' },
    { id: 'nytm-tasks-h', label: 'Tasks (High)', icon: ListTodo, color: 'text-green-600' },
    { id: 'nytm-tasks-m', label: 'Tasks (Med)', icon: ListTodo, color: 'text-green-400' },
    { id: 'nytm-tasks-l', label: 'Tasks (Low)', icon: ListTodo, color: 'text-green-200' },
    { id: 'nytm-flight', label: 'Flights', icon: Plane, color: 'text-sky-500' },
    { id: 'nytm-hotel', label: 'Hotels', icon: Bed, color: 'text-indigo-500' },
  ];

  const toggleType = (id) => {
    setSelectedTypes(prev => 
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);

    try {
      const feedId = 'feed_' + Math.random().toString(36).substr(2, 9);
      const token = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      const newFeed = {
        id: feedId,
        name,
        note,
        types: selectedTypes,
        token,
        createdAt: new Date().toISOString()
      };

      const existingFeeds = profile?.exportedCalendars || {};
      await saveUserProfile({
        exportedCalendars: {
          ...existingFeeds,
          [feedId]: newFeed
        }
      });

      const url = `https://calendarfeed-ipldpbicfq-uc.a.run.app?uid=${user.uid}&feed=${feedId}&token=${token}`;
      setGeneratedUrl(url);
    } catch (error) {
      console.error('Error saving calendar feed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (generatedUrl) {
    return (
      <DraggableModal onClose={onClose}>
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-zinc-900 tracking-tight">Calendar Exported!</h2>
            <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-full transition-colors">
              <X size={20} strokeWidth={2.5} />
            </button>
          </div>

          <div className="p-4 bg-zinc-50 border border-zinc-100 rounded-2xl">
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2">
              Your Feed URL
            </p>
            <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl p-2">
              <input
                readOnly
                value={generatedUrl}
                className="flex-1 text-[10px] font-mono text-zinc-600 bg-transparent outline-none"
              />
              <button
                onClick={copyToClipboard}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-400 hover:text-zinc-900"
              >
                {copied ? <Check size={16} strokeWidth={2.5} className="text-green-500" /> : <Copy size={16} strokeWidth={2.5} />}
              </button>
            </div>
          </div>

          <p className="text-xs text-zinc-500 leading-relaxed italic">
            Copy this URL into Google Calendar (Add by URL) or Apple Calendar to see your NYTM events.
          </p>

          <button
            onClick={onClose}
            className="w-full py-3 bg-zinc-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-zinc-200"
          >
            Done
          </button>
        </div>
      </DraggableModal>
    );
  }

  return (
    <DraggableModal onClose={onClose}>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-zinc-900 tracking-tight">Export Calendar</h2>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-full transition-colors">
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 block">
              Calendar Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My Shared Calendar"
              className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3 text-sm font-bold outline-none focus:border-zinc-900 transition-colors"
            />
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 block">
              Personal Note (Who is this shared with?)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Shared with family for the summer trip"
              className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3 text-sm font-bold outline-none focus:border-zinc-900 transition-colors h-20 resize-none"
            />
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-3 block">
              Included Event Types
            </label>
            <div className="grid grid-cols-1 gap-2">
              {options.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => toggleType(opt.id)}
                  className={`flex items-center gap-3 p-3 rounded-2xl border transition-all ${
                    selectedTypes.includes(opt.id)
                      ? 'bg-zinc-900 border-zinc-900 text-white shadow-md'
                      : 'bg-zinc-50 border-zinc-100 text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  <opt.icon size={18} strokeWidth={2.5} className={selectedTypes.includes(opt.id) ? 'text-white' : opt.color} />
                  <span className="text-[10px] font-black uppercase tracking-widest flex-1 text-left">
                    {opt.label}
                  </span>
                  {selectedTypes.includes(opt.id) && <Check size={14} strokeWidth={3} />}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving || !name.trim() || selectedTypes.length === 0}
          className="w-full py-4 bg-zinc-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-zinc-200 mt-2"
        >
          {isSaving ? 'Generating...' : 'Generate Calendar Link'}
        </button>
      </div>
    </DraggableModal>
  );
};

export default ExportCalendarModal;